import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { searchRequest } from 'core/services/product';
import { listRequest, reportRequest } from 'core/services/admin';
import { LoadingState } from 'types/domain/base';
import { ISearch, ISearchResponse, SearchType, SortBy } from 'types/domain/search';

export interface IComponentPagination {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  loading: boolean;
  ids: number[]; // Store IDs returned from server for this page
  searchTerm?: string; // Component-specific search term
}

export interface ISearchReducer {
  term: string; // Deprecated: kept for backwards compatibility
  filter: number;
  sort: SortBy | number;
  response: ISearchResponse;
  loading: LoadingState;
  searchType: SearchType;
  selected: ISearchSelection[];
  components: Record<string, IComponentPagination>;
}

export interface ISearchSelection {
  moduleId: number;
  actionId: number;
  index: number;
  created: number;
}

const initialState: ISearchReducer = {
  term: null,
  filter: null,
  sort: SortBy.MODIFIED,
  response: null,
  loading: LoadingState.DEFAULT,
  searchType: null,
  selected: [],
  components: {},
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    updateSearch(state, action: PayloadAction<ISearch>) {
      // Update global search state for backwards compatibility
      const { term, componentId, ...rest } = action.payload;

      // If componentId is provided, update component-specific search term
      if (componentId) {
        if (!state.components[componentId]) {
          state.components[componentId] = {
            currentPage: 0,
            pageSize: 10,
            totalRecords: 0,
            loading: true,
            ids: [],
            searchTerm: term || '',
          };
        } else {
          state.components[componentId].searchTerm = term || '';
          state.components[componentId].loading = true;
        }
      }

      // Also update global state for backwards compatibility (mutate, don't return)
      state.term = term;
      state.response = null;
      Object.assign(state, rest);
    },

    selectSearchResult(state, action: PayloadAction<ISearchSelection>) {
      state.selected.push(action.payload);
    },
    clearSearchLoading(state, action: PayloadAction<{ componentId: string }>) {
      const { componentId } = action.payload;
      if (componentId && state.components[componentId]) {
        state.components[componentId].loading = false;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(searchRequest.pending, (state, action: any) => {
        state.loading = LoadingState.LOADING;
        // Handle componentId for pagination if provided
        const { componentId, pagerInfo, term } = action.meta.arg;
        if (componentId) {
          if (!state.components[componentId]) {
            state.components[componentId] = {
              currentPage: pagerInfo?.page || 0,
              pageSize: pagerInfo?.pageCount || 10,
              totalRecords: 0,
              loading: true,
              ids: [],
              searchTerm: term || '',
            };
          } else {
            state.components[componentId].loading = true;
            state.components[componentId].searchTerm = term || '';
          }
        }
      })
      .addCase(searchRequest.rejected, (state, action: any) => {
        state.loading = LoadingState.ERROR;
        // Handle componentId for pagination if provided
        const { componentId } = action.meta.arg;
        if (componentId && state.components[componentId]) {
          state.components[componentId].loading = false;
          state.components[componentId].ids = [];
        }
      })
      .addCase(searchRequest.fulfilled, (state, action: any) => {
        state.loading = LoadingState.LOADED;
        state.response = action.payload.searchResponse;
        // Handle componentId for pagination if provided
        const { componentId, pagerInfo, term } = action.meta.arg;
        if (componentId && action.payload.searchResponse?.results) {
          // Extract IDs from the search results list
          const ids = action.payload.searchResponse.results.list
            ? action.payload.searchResponse.results.list.map((item: any) => item.id)
            : [];
          state.components[componentId] = {
            currentPage: pagerInfo?.page || 0,
            pageSize: pagerInfo?.pageCount || 10,
            totalRecords: action.payload.searchResponse.results.pagerInfo?.totalRecords || ids.length,
            loading: false,
            ids,
            searchTerm: term || '',
          };
        }
      })
      .addCase(listRequest.pending, (state, action: any) => {
        const { componentId, pagerInfo, search } = action.meta.arg;
        if (componentId) {
          if (!state.components[componentId]) {
            state.components[componentId] = {
              currentPage: pagerInfo?.page || 0,
              pageSize: pagerInfo?.pageCount || 10,
              totalRecords: 0,
              loading: true,
              ids: [],
              searchTerm: search || '',
            };
          } else {
            state.components[componentId].loading = true;
            state.components[componentId].currentPage = pagerInfo?.page || state.components[componentId].currentPage;
            state.components[componentId].pageSize = pagerInfo?.pageCount || state.components[componentId].pageSize;
            state.components[componentId].searchTerm = search || '';
          }
        }
      })
      .addCase(listRequest.fulfilled, (state, action: any) => {
        const { componentId, pagerInfo, search } = action.meta.arg;
        if (componentId) {
          // Extract IDs from the returned list
          const ids = action.payload.list ? action.payload.list.map((item: any) => item.id) : [];
          state.components[componentId] = {
            currentPage: pagerInfo.page,
            pageSize: pagerInfo.pageCount || 10,
            totalRecords: action.payload.pagerInfo?.totalRecords || 0,
            loading: false,
            ids,
            searchTerm: search || '',
          };
        }
      })
      .addCase(listRequest.rejected, (state, action: any) => {
        const { componentId } = action.meta.arg;
        if (componentId && state.components[componentId]) {
          state.components[componentId].loading = false;
          state.components[componentId].ids = [];
        }
      })
      // Add listeners for reportRequest (same pattern as listRequest)
      .addCase(reportRequest.pending, (state, action: any) => {
        const { componentId, pagerInfo, search } = action.meta.arg;
        if (componentId) {
          if (!state.components[componentId]) {
            state.components[componentId] = {
              currentPage: pagerInfo?.page || 0,
              pageSize: pagerInfo?.pageCount || 10,
              totalRecords: 0,
              loading: true,
              ids: [],
              searchTerm: search || '',
            };
          } else {
            state.components[componentId].loading = true;
            state.components[componentId].currentPage = pagerInfo?.page || state.components[componentId].currentPage;
            state.components[componentId].pageSize = pagerInfo?.pageCount || state.components[componentId].pageSize;
            state.components[componentId].searchTerm = search || '';
          }
        }
      })
      .addCase(reportRequest.fulfilled, (state, action: any) => {
        const { componentId, pagerInfo, search } = action.meta.arg;

        if (componentId) {
          // Extract IDs from the returned list
          const ids = action.payload.list ? action.payload.list.map((item: any) => item.id) : [];

          state.components[componentId] = {
            currentPage: pagerInfo.page,
            pageSize: pagerInfo.pageCount || 10,
            totalRecords: action.payload.pagerInfo?.totalRecords || 0,
            loading: false,
            ids,
            searchTerm: search || '',
          };
        }
      })
      .addCase(reportRequest.rejected, (state, action: any) => {
        const { componentId } = action.meta.arg;
        if (componentId && state.components[componentId]) {
          state.components[componentId].loading = false;
          state.components[componentId].ids = [];
        }
      });
  },
});

export const { updateSearch, selectSearchResult, clearSearchLoading } = searchSlice.actions;

// Selector to get component pagination state
export const selectComponentPagination = (componentId: string) => (state: any) =>
  state.core.search.components[componentId] || {
    currentPage: 0,
    pageSize: 10,
    totalRecords: 0,
    loading: false,
    ids: [],
    searchTerm: '',
  };

export default searchSlice.reducer;
